<template>
  <div>
    <div class="h3">
      Enfant {{ idxEnfant }}
    </div>

    <!-- Nom -->
    <input-text
      label="Nom de votre enfant"
      slug="nom"
      :model="nom"
      :v$="v$"
      @input="$emit('update:nom', $event.target.value)"
    />

    <!-- Prénom -->
    <input-text
      label="Prénom de votre enfant"
      slug="prenom"
      :model="prenom"
      :v$="v$"
      @input="$emit('update:prenom', $event.target.value)"
    />

    <!-- Sexe -->
    <input-radio
      :id="`sexeEnfant${idxEnfant}`"
      label="Le sexe de votre enfant"
      slug="sexe"
      :model="sexe"
      :options="[{'label': 'Masculin', 'value': 'M'},{'label': 'Féminim', 'value': 'F'},]"
      :v$="v$"
      @input="$emit('update:sexe', $event.target.value)"
    />

    <!-- Date de naissance -->
    <input-text-date-picker
      label="Date de naissance de votre enfant"
      slug="dateDeNaissance"
      :model="dateDeNaissance"
      :v$="v$"
      :from-date="dateDeNaissanceFrom"
      :to-date="dateDeNaissanceTo"
      @input="$emit('update:dateDeNaissance', $event.target.value)"
      @update-date="$emit('update:dateDeNaissance', $event)"
    />

    <!-- Ayant droit -->
    <input-radio
      :id="`ayantDroitEnfant${idxEnfant}`"
      label="Votre enfant est-il un ayant droit ?"
      slug="ayantDroit"
      :model="ayantDroit"
      :options="ayantDroitOptions"
      :v$="v$"
      @input="$emit('update:ayantDroit', $event.target.value)"
    />

    <!-- Numéro de sécurité social -->
    <InputText
      v-if="ayantDroit === '0'"
      v-maska="'# ## ## ## ### ### ##'"
      label="Le numéro de sécurité sociale de votre enfant"
      slug="numSecu"
      :model="numSecu"
      :v$="v$"
      @input="$emit('update:numSecu', $event.target.value)"
    />

    <!-- Organisme de rattachement -->
    <InputText
      v-if="ayantDroit === '0'"
      v-maska="'## ### ####'"
      label="Le régime/Caisse/Centre de rattachement de votre enfant"
      slug="organisme"
      :model="organisme"
      :v$="v$"
      @input="$emit('update:organisme', $event.target.value)"
    />
  </div>
</template>

<script>

import useVuelidate from '@vuelidate/core'
import {
  childMustBeLessThan20AtEffectiveDate,
  frenchSocialSecurityNumber,
  required,
  requiredIf,
  serverError
} from "src/modules/shared/validators/validators";
import {computed, ref, watch} from "vue";
import InputRadio from "src/modules/shared/components/Form/InputRadio";
import InputText from "src/modules/shared/components/Form/InputText";
import InputTextDatePicker from "src/modules/shared/components/Form/InputTextDatePicker";
import {
  dateDeNaissanceEnfantFromFn,
  dateDeNaissanceEnfantToFn
} from "src/modules/souscription/utils/dateDeNaissanceEnfant";
import {ayantDroitOptionsFn} from "src/modules/souscription/utils/ayantDroitOptions";

export default {
  name: "ChildForm",
  components: {
    InputRadio,
    InputText,
    InputTextDatePicker
  },
  props: {
    serverErrors: {
      type: Array,
      required: true
    },
    displayConjoint: {
      type: Boolean,
      required: true
    },
    idxEnfant: {
      type: Number,
      default: null
    },
    nom: {
      type: String,
      default: ''
    },
    prenom: {
      type: String,
      default: ''
    },
    sexe: {
      type: String,
      default: ''
    },
    dateDeNaissance: {
      type: String,
      default: ''
    },
    ayantDroit: {
      type: String,
      default: ''
    },
    numSecu: {
      type: String,
      required: true,
      default: ''
    },
    organisme: {
      type: String,
      required: true,
      default: ''
    },
    dateEffet: {
      type: String,
      required: true
    }
  },
  emits: [
    'update:nom',
    'update:prenom',
    'update:sexe',
    'update:dateDeNaissance',
    'update:ayantDroit',
    'update:numSecu',
    'update:organisme',
  ],
  setup(props, {emit}) {
    const ayantDroitOptions = ref(ayantDroitOptionsFn(props.dateEffet, props.dateDeNaissance, props.displayConjoint))

    const dateDeNaissanceFrom = dateDeNaissanceEnfantFromFn(props.dateEffet)
    const dateDeNaissanceTo = dateDeNaissanceEnfantToFn()

    const ayantDroitsOptionWatcher = watch(
        [
          () => props.dateEffet,
          () => props.dateDeNaissance,
          () => props.displayConjoint
        ],
        () => {
          console.debug('[ChildForm]', 'ayantDroitOptions', props.dateEffet, props.dateDeNaissance, props.displayConjoint)
          ayantDroitOptions.value = ayantDroitOptionsFn(props.dateEffet, props.dateDeNaissance, props.displayConjoint)

          if (ayantDroitOptions.value.length === 1) {
            emit('update:ayantDroit', ayantDroitOptions.value[0].value)
          }
        }
    )

    const validations = computed(() => ({
      nom: {
        required,
        serverError: serverError(props, `nomEnfant${props.idxEnfant}`)
      },
      prenom: {
        required,
        serverError: serverError(props, `prenomEnfant${props.idxEnfant}`)
      },
      sexe: {
        required,
        serverError: serverError(props, `sexeEnfant${props.idxEnfant}`)
      },
      dateDeNaissance: {
        required,
        childMustBeLessThan20AtEffectiveDate: childMustBeLessThan20AtEffectiveDate(props.dateEffet),
        serverError: serverError(props, `dateDeNaissanceEnfant${props.idxEnfant}`)
      },
      ayantDroit: {
        required,
        serverError: serverError(props, `ayantDroitEnfant${props.idxEnfant}`)
      },
      numSecu: {
        requiredIf: requiredIf(props.ayantDroit === '0'),
        frenchSocialSecurityNumber,
        serverError: serverError(props, `numSecuEnfant${props.idxEnfant}`)
      },
      organisme: {
        requiredIf: requiredIf(props.ayantDroit === '0'),
        serverError: serverError(props, `organismeEnfant${props.idxEnfant}`)
      },
    }))

    const v$ = useVuelidate(validations, props)

    return {
      ayantDroitOptions,
      ayantDroitsOptionWatcher,
      dateDeNaissanceFrom,
      dateDeNaissanceTo,
      v$
    }
  },
  watch: {
    serverErrors() {
      this.v$.$reset()
      this.v$.$touch()
    }
  },
}
</script>

<style scoped>

</style>
